import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Card,
  Descriptions,
  Tag,
  Skeleton,
  Space,
  Button,
  message,
  Image,
  Typography,
  Empty,
  Popconfirm,
} from 'antd';
import {
  ArrowLeftOutlined,
  DeleteOutlined,
  StopOutlined,
  RedoOutlined,
} from '@ant-design/icons';
import {
  getTask,
  deleteTask,
  cancelTask,
  retryTask,
  toOutputImages,
  type Task,
} from '@/api/tasks';
import { formatDateTime } from '@/utils/format';

// in-flight 状态: 可取消。
const CANCELLABLE = new Set(['queued', 'running', 'warming']);

const statusColor: Record<string, string> = {
  done: 'green',
  running: 'blue',
  queued: 'gold',
  failed: 'red',
  cancelled: 'default',
};

function prettyJSON(v: unknown): string {
  if (v === undefined || v === null) return '-';
  try {
    return JSON.stringify(v, null, 2);
  } catch {
    return String(v);
  }
}

export function TaskDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [task, setTask] = useState<Task | null>(null);
  const [loading, setLoading] = useState(false);
  const [acting, setActing] = useState(false);

  const load = () => {
    if (!id) return;
    setLoading(true);
    getTask(id)
      .then(setTask)
      .catch((e) => message.error(e instanceof Error ? e.message : '加载失败'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleDelete = async () => {
    if (!id) return;
    setActing(true);
    try {
      await deleteTask(id);
      message.success('任务已删除');
      navigate('/tasks');
    } catch (e) {
      message.error(e instanceof Error ? e.message : '删除失败');
    } finally {
      setActing(false);
    }
  };

  const handleCancel = async () => {
    if (!id) return;
    setActing(true);
    try {
      const updated = await cancelTask(id);
      setTask(updated);
      message.success('任务已取消 (已退还钻石)');
    } catch (e) {
      message.error(e instanceof Error ? e.message : '取消失败');
    } finally {
      setActing(false);
    }
  };

  const handleRetry = async () => {
    if (!id) return;
    setActing(true);
    try {
      const created = await retryTask(id);
      message.success('已重试 (admin 重跑, 未扣费)');
      if (created?.id) navigate(`/tasks/${created.id}`);
      else load();
    } catch (e) {
      message.error(e instanceof Error ? e.message : '重试失败');
    } finally {
      setActing(false);
    }
  };

  const canCancel = task ? CANCELLABLE.has(task.status) : false;
  // 终态均可重试 (done 也可能需重跑: 出图不满意 / provider 静默降级出无效结果); 只在途不行。
  const canRetry = task ? (task.status === 'failed' || task.status === 'done' || task.status === 'cancelled') : false;

  return (
    <div>
      <Space style={{ marginBottom: 16 }} wrap>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/tasks')}>
          返回列表
        </Button>
        {canCancel && (
          <Popconfirm
            title="取消任务"
            description="将终止工作流并退还已扣钻石，确认取消？"
            okText="确认取消"
            cancelText="再想想"
            onConfirm={handleCancel}
          >
            <Button danger icon={<StopOutlined />} loading={acting}>
              取消任务
            </Button>
          </Popconfirm>
        )}
        {canRetry && (
          <Popconfirm
            title="重试任务"
            description="将以原输入重跑（admin 操作，不向用户扣费），确认？"
            okText="确认重试"
            cancelText="取消"
            onConfirm={handleRetry}
          >
            <Button icon={<RedoOutlined />} loading={acting}>
              重试 (不扣费)
            </Button>
          </Popconfirm>
        )}
        <Popconfirm
          title="删除任务"
          description="软删除该任务（不可在前台恢复），确认删除？"
          okText="确认删除"
          cancelText="取消"
          okButtonProps={{ danger: true }}
          onConfirm={handleDelete}
        >
          <Button danger icon={<DeleteOutlined />} loading={acting}>
            删除
          </Button>
        </Popconfirm>
      </Space>
      {loading || !task ? (
        <Skeleton active />
      ) : (
        <Space direction="vertical" size={16} style={{ display: 'flex' }}>
          <Card
            title={`任务 ${task.id}`}
            extra={<Tag color={statusColor[task.status] || 'default'}>{task.status}</Tag>}
          >
            <Descriptions column={2} size="small">
              <Descriptions.Item label="用户 ID">
                <Link to={`/users/${task.user_id}`}>{task.user_id}</Link>
              </Descriptions.Item>
              <Descriptions.Item label="App">app#{task.app_id}</Descriptions.Item>
              <Descriptions.Item label="工作流">
                <Tag>{task.workflow_code}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="套系">
                {task.package_id ? (
                  <span title={`#${task.package_id}`}>
                    {task.package_name || `#${task.package_id}`}
                  </span>
                ) : (
                  '-'
                )}
              </Descriptions.Item>
              <Descriptions.Item label="工作流运行 ID">
                {task.workflow_id || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="优先级">{task.priority ?? '-'}</Descriptions.Item>
              <Descriptions.Item label="成本(元)">{task.cost_cny ?? '-'}</Descriptions.Item>
              <Descriptions.Item label="耗时(ms)">{task.duration_ms ?? '-'}</Descriptions.Item>
              <Descriptions.Item label="开始时间">
                {formatDateTime(task.started_at)}
              </Descriptions.Item>
              <Descriptions.Item label="完成时间">
                {formatDateTime(task.finished_at)}
              </Descriptions.Item>
              <Descriptions.Item label="创建时间">
                {formatDateTime(task.created_at)}
              </Descriptions.Item>
              <Descriptions.Item label="更新时间">
                {formatDateTime(task.updated_at)}
              </Descriptions.Item>
            </Descriptions>
          </Card>

          {(task.error_code || task.error_msg) && (
            <Card title="错误" size="small">
              <Descriptions column={1} size="small">
                <Descriptions.Item label="error_code">
                  <Tag color="red">{task.error_code || '-'}</Tag>
                </Descriptions.Item>
                <Descriptions.Item label="error_msg">{task.error_msg || '-'}</Descriptions.Item>
              </Descriptions>
            </Card>
          )}

          <Card title={`源图 (${task.source_images?.length ?? 0})`}>
            {task.source_images && task.source_images.length > 0 ? (
              <Image.PreviewGroup>
                <Space wrap size={12}>
                  {task.source_images.map((s, i) => (
                    <Image
                      key={i}
                      src={s.thumb_url || s.medium_url || s.url}
                      width={160}
                      style={{ objectFit: 'cover', borderRadius: 6 }}
                      preview={{ src: s.url }}
                    />
                  ))}
                </Space>
              </Image.PreviewGroup>
            ) : (
              <Empty description="无源图 (该工作流不需要上传图)" />
            )}
          </Card>

          <Card title={`结果图 (${toOutputImages(task.outputs).length})`}>
            {toOutputImages(task.outputs).length > 0 ? (
              <Image.PreviewGroup>
                <Space wrap size={12}>
                  {toOutputImages(task.outputs).map((o, i) => {
                    const display = o.medium_url || o.url || o.thumb_url;
                    return display ? (
                      <Image
                        key={i}
                        src={o.thumb_url || display}
                        width={160}
                        style={{ objectFit: 'cover', borderRadius: 6 }}
                        preview={{ src: o.url || display }}
                      />
                    ) : (
                      <Typography.Text key={i} code style={{ wordBreak: 'break-all' }}>
                        {/* 无法渲染时显示原始 url 文本 */}
                        {String((o as Record<string, unknown>).url ?? JSON.stringify(o))}
                      </Typography.Text>
                    );
                  })}
                </Space>
              </Image.PreviewGroup>
            ) : (
              <Empty description="暂无结果图" />
            )}
          </Card>

          <Card title="输入 (input)" size="small">
            <Typography.Paragraph>
              <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                {prettyJSON(task.input)}
              </pre>
            </Typography.Paragraph>
          </Card>

          <Card title="Provider 链路 (provider_chain_used)" size="small">
            <Typography.Paragraph>
              <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                {prettyJSON(task.provider_chain_used)}
              </pre>
            </Typography.Paragraph>
          </Card>
        </Space>
      )}
    </div>
  );
}
